import { AttendanceRecord, CalendarEvent } from '../types';

/**
 * Calculates which occurrence of Saturday in a month a given date is (1st, 2nd, 3rd, 4th, or 5th Saturday).
 * Days 1-7 = 1st, 8-14 = 2nd, 15-21 = 3rd, 22-28 = 4th, 29-31 = 5th.
 */
export const getSaturdayOccurrence = (date: Date): number => {
  return Math.ceil(date.getDate() / 7);
};

/**
 * Extracts a Set of all YYYY-MM-DD date strings marked as whole-school or class-specific
 * student non-instructional holidays/urgent-offs.
 */
export const getHolidayDatesSet = (
  events: CalendarEvent[] = [],
  classId?: string
): Set<string> => {
  const holidayDates = new Set<string>();

  events.forEach(event => {
    if (event.type === 'holiday' || event.type === 'urgent_off') {
      const isTargeted =
        !event.targetClassIds ||
        event.targetClassIds.length === 0 ||
        (classId && event.targetClassIds.includes(classId));

      if (isTargeted && event.startDate) {
        const start = new Date(event.startDate + 'T00:00:00');
        const end = new Date((event.endDate || event.startDate) + 'T00:00:00');

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          holidayDates.add(`${yyyy}-${mm}-${dd}`);
        }
      }
    }
  });

  return holidayDates;
};

/**
 * Determines whether a specific calendar date is an official Student Instructional Day.
 */
export const isStudentInstructionalDay = (
  date: Date,
  offSaturdays: number[] = [2, 4],
  holidayDatesSet: Set<string>
): boolean => {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const dateStr = `${yyyy}-${mm}-${dd}`;

  // 1. Sundays are always non-instructional
  if (dayOfWeek === 0) return false;

  // 2. Off-Saturdays check based on school's Saturday Policy
  if (dayOfWeek === 6) {
    const occurrence = getSaturdayOccurrence(date);
    if (offSaturdays.includes(occurrence)) {
      return false;
    }
  }

  // 3. Official Declared Student Holidays or Urgent Offs
  if (holidayDatesSet.has(dateStr)) {
    return false;
  }

  return true;
};

/**
 * Returns all official student instructional dates between the Academic Year start date
 * and the evaluation date (capped at today if the academic year is ongoing).
 */
export const getInstructionalDatesToDate = (
  academicYearStartDate: string,
  academicYearEndDate?: string,
  offSaturdays: number[] = [2, 4],
  events: CalendarEvent[] = [],
  classId?: string,
  asOfDate: Date | string = new Date()
): string[] => {
  if (!academicYearStartDate) return [];

  let asOfStr: string;
  if (typeof asOfDate === 'string') {
    asOfStr = asOfDate.includes('T') ? asOfDate.split('T')[0] : asOfDate;
  } else if (asOfDate instanceof Date && !isNaN(asOfDate.getTime())) {
    asOfStr = asOfDate.toISOString().split('T')[0];
  } else {
    asOfStr = new Date().toISOString().split('T')[0];
  }

  const cleanStartDate = academicYearStartDate.includes('T') ? academicYearStartDate.split('T')[0] : academicYearStartDate;
  const start = new Date(cleanStartDate + 'T00:00:00');
  if (isNaN(start.getTime())) return [];

  const cleanEndDate = academicYearEndDate ? (academicYearEndDate.includes('T') ? academicYearEndDate.split('T')[0] : academicYearEndDate) : undefined;
  const capDateStr = cleanEndDate && cleanEndDate < asOfStr ? cleanEndDate : asOfStr;
  const end = new Date(capDateStr + 'T00:00:00');
  if (isNaN(end.getTime())) return [];

  if (start > end) return [];

  const holidaysSet = getHolidayDatesSet(events, classId);
  const instructionalDates: string[] = [];

  const current = new Date(start);
  while (current <= end) {
    if (isStudentInstructionalDay(current, offSaturdays, holidaysSet)) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      instructionalDates.push(`${yyyy}-${mm}-${dd}`);
    }
    current.setDate(current.getDate() + 1);
  }

  return instructionalDates;
};

export interface StudentAttendanceMetrics {
  present: number;
  late: number;
  absent: number;
  totalRecorded: number;
  schoolDaysToDate: number;
  effectiveTotalDays: number;
  percentage: number;
  statusThreshold: 'healthy' | 'warning' | 'critical';
}

/**
 * Calculates a student's running attendance metrics against official school working days to date.
 */
export const computeStudentAttendanceMetrics = (
  studentId: string,
  records: AttendanceRecord[],
  instructionalDates: string[]
): StudentAttendanceMetrics => {
  const studentRecords = records.filter(r => r.studentId === studentId);
  const present = studentRecords.filter(r => r.status === 'present').length;
  const late = studentRecords.filter(r => r.status === 'late').length;
  const absent = studentRecords.filter(r => r.status === 'absent').length;
  const totalRecorded = present + late + absent;

  const schoolDaysToDate = instructionalDates.length;
  // Use official school days, or total recorded if teacher recorded for extra days/tests
  const effectiveTotalDays = Math.max(schoolDaysToDate, totalRecorded);
  const attended = present + late; // Late counted as full for now per specification

  const percentage = effectiveTotalDays > 0 ? (attended / effectiveTotalDays) * 100 : 0;
  const roundedPct = Math.round(percentage * 10) / 10;

  let statusThreshold: 'healthy' | 'warning' | 'critical' = 'healthy';
  if (roundedPct < 65) {
    statusThreshold = 'critical';
  } else if (roundedPct < 75) {
    statusThreshold = 'warning';
  }

  return {
    present,
    late,
    absent,
    totalRecorded,
    schoolDaysToDate,
    effectiveTotalDays,
    percentage: roundedPct,
    statusThreshold
  };
};

export interface MonthLedgerRow {
  monthKey: string; // YYYY-MM
  monthName: string; // "June 2026"
  schoolDays: number;
  present: number;
  late: number;
  absent: number;
  percentage: number;
  statusThreshold: 'healthy' | 'warning' | 'critical';
}

/**
 * Computes month-by-month tabular attendance breakdown for parents and teachers.
 */
export const computeMonthlyAttendanceLedger = (
  studentId: string,
  records: AttendanceRecord[],
  instructionalDates: string[]
): MonthLedgerRow[] => {
  const studentRecords = records.filter(r => r.studentId === studentId);

  // Group instructional dates by month YYYY-MM
  const monthsMap = new Map<string, { days: string[] }>();
  instructionalDates.forEach(dateStr => {
    const monthKey = dateStr.slice(0, 7);
    if (!monthsMap.has(monthKey)) {
      monthsMap.set(monthKey, { days: [] });
    }
    monthsMap.get(monthKey)!.days.push(dateStr);
  });

  // Also make sure any month with recorded attendance is included
  studentRecords.forEach(r => {
    const monthKey = r.date.slice(0, 7);
    if (!monthsMap.has(monthKey)) {
      monthsMap.set(monthKey, { days: [] });
    }
  });

  const sortedMonthKeys = Array.from(monthsMap.keys()).sort();

  return sortedMonthKeys.map(monthKey => {
    const [yearStr, monthStr] = monthKey.split('-');
    const dateObj = new Date(parseInt(yearStr), parseInt(monthStr) - 1, 1);
    const monthName = dateObj.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

    const monthInstructionalDays = monthsMap.get(monthKey)?.days.length || 0;
    const monthRecords = studentRecords.filter(r => r.date.startsWith(monthKey));

    const present = monthRecords.filter(r => r.status === 'present').length;
    const late = monthRecords.filter(r => r.status === 'late').length;
    const absent = monthRecords.filter(r => r.status === 'absent').length;

    const denominator = Math.max(monthInstructionalDays, present + late + absent);
    const attended = present + late;
    const percentage = denominator > 0 ? Math.round(((attended / denominator) * 100) * 10) / 10 : 0;

    let statusThreshold: 'healthy' | 'warning' | 'critical' = 'healthy';
    if (percentage < 65) {
      statusThreshold = 'critical';
    } else if (percentage < 75) {
      statusThreshold = 'warning';
    }

    return {
      monthKey,
      monthName,
      schoolDays: monthInstructionalDays,
      present,
      late,
      absent,
      percentage,
      statusThreshold
    };
  });
};
